import { motion } from 'framer-motion'
import type { ReactNode } from 'react'

import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { useMotionPreference } from '@/hooks/useMotionPreference'
import { DURATION, EASE } from '@/lib/motion'

type ErrorAction = {
  label: string
  to: string
  variant?: 'primary' | 'secondary' | 'ghost'
}

type ErrorStateProps = {
  icon: ReactNode
  title: string
  description: string
  actions: ErrorAction[]
}

export function ErrorState({ icon, title, description, actions }: ErrorStateProps) {
  const reduceMotion = useMotionPreference()

  const card = (
    <Card className="max-w-2xl p-8 sm:p-10">
      <div className="mb-6 inline-flex h-14 w-14 items-center justify-center border border-brand/20 bg-brand/5 text-brand">
        {icon}
      </div>
      <h1 className="text-3xl font-semibold tracking-tight text-slate-950">{title}</h1>
      <p className="mt-4 text-base leading-7 text-slate-600">{description}</p>
      <div className="mt-8 flex flex-wrap gap-4">
        {actions.map((action) => (
          <Button key={action.label} to={action.to} variant={action.variant ?? 'primary'}>
            {action.label}
          </Button>
        ))}
      </div>
    </Card>
  )

  if (reduceMotion) {
    return card
  }

  return (
    <motion.div
      className="w-full max-w-2xl"
      initial={{ opacity: 0, y: 24, scale: 0.985 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: DURATION.reveal, ease: EASE }}
    >
      {card}
    </motion.div>
  )
}
